import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ExternalLink, Calendar } from "lucide-react";
import { useToast } from "@core/hooks/use-toast";
import {
  getAllPolls,
  savePolls,
  type Poll as StoragePoll,
} from "@core/lib/pollStorage";
import { getCurrentUserId } from "@core/lib/guestIdentityService";
import { useAuth } from "@core/contexts/AuthContext";
import { Button } from "@core/components/ui/button";
import { Input } from "@core/components/ui/input";
import { Textarea } from "@core/components/ui/textarea";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@core/components/ui/card";
import { Label } from "@core/components/ui/label";
import {
  SchedulingRulesForm,
  type SchedulingRules,
} from "./SchedulingRulesForm";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@core/components/ui/tooltip";
import PollSettingsForm from "@core/components/polls/PollSettingsForm";
import {
  Settings as SettingsIcon,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@core/components/ui/collapsible";
import { motion } from "framer-motion";
import { Mail, X } from "lucide-react";
import { guestEmailService } from "@core/lib/guestEmailService";
import { SafeguardSection } from "@core/components/polls/SafeguardSection";
import { AvailabilityDashboard } from "./AvailabilityDashboard";
import { googleCalendar } from "@core/lib/google-calendar";
import { addDays } from "date-fns";
import { logError, ErrorFactory } from "@core/lib/error-handling";
import { formatDateLocal } from "@core/lib/date-utils";

// Type local pour éviter la dépendance cyclique
type AvailabilityPollSettings = any;

// Fonction pour générer des dates par défaut pour les availability polls
const generateDefaultDates = () => {
  const dates = [];
  const today = new Date();

  // Générer 14 jours à partir d'aujourd'hui
  for (let i = 1; i <= 14; i++) {
    const date = addDays(today, i);
    dates.push(formatDateLocal(date));
  }

  return dates;
};

interface AvailabilityPollCreatorContentProps {
  onBack?: (createdPoll?: StoragePoll) => void;
  initialData?: {
    title?: string;
    description?: string;
  };
  onCreate?: (poll: StoragePoll) => void;
}

export const AvailabilityPollCreatorContent: React.FC<
  AvailabilityPollCreatorContentProps
> = ({ onBack, initialData, onCreate }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, signInWithGoogle } = useAuth();

  const [title, setTitle] = useState(initialData?.title || "");
  const [description, setDescription] = useState(
    initialData?.description || "",
  );
  const [schedulingRules, setSchedulingRules] = useState<SchedulingRules>({
    minLatencyMinutes: 15,
    maxLatencyMinutes: 30,
    preferNearTerm: true,
    preferHalfDays: false,
    slotDurationMinutes: 60,
  });
  const [advancedSettings, setAdvancedSettings] =
    useState<AvailabilityPollSettings>({
      showLogo: true,
      requireAuth: false,
      oneResponsePerPerson: false,
      allowEditAfterSubmit: false,
      maxResponses: undefined,
      resultsVisibility: "public",
    });
  const [published, setPublished] = useState(false);
  const [publishedPoll, setPublishedPoll] = useState<StoragePoll | null>(null);
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const [busySlots, setBusySlots] = useState<
    Array<{ start: string; end: string }>
  >([]);

  // Fetch busy slots logic
  React.useEffect(() => {
    const fetchBusy = async () => {
      if (user) {
        try {
          // Vérifier si l'utilisateur a déjà connecté son calendrier
          // Note: googleCalendar.hasCalendarAccess() vérifie le token en local/session
          const hasAccess = await googleCalendar.hasCalendarAccess();
          if (hasAccess) {
            const now = new Date();
            const end = addDays(now, 60); // Fetch next 2 months
            const busy = await googleCalendar.getFreeBusy(
              now.toISOString(),
              end.toISOString(),
            );
            setBusySlots(busy);
          }
        } catch (e) {
          logError(
            ErrorFactory.api(
              "Failed to fetch busy slots",
              "Erreur récupération agenda",
              {
                error: e,
              },
            ),
            {
              component: "AvailabilityPollCreatorContent",
              operation: "fetchBusy",
            },
          );
        }
      }
    };
    fetchBusy();
  }, [user]);

  const handleCreate = (asDraft = false) => {
    if (!title.trim()) {
      toast({
        title: "Titre requis",
        description:
          "Veuillez entrer un titre pour votre sondage disponibilités.",
        variant: "destructive",
      });
      return;
    }

    const all = getAllPolls();
    const now = new Date().toISOString();
    const random = Math.random().toString(36).slice(2);
    const slug =
      title
        .toLowerCase()
        .trim()
        .replace(/\s+/g, "-")
        .replace(/[^a-z0-9-]/g, "") + `-${Date.now().toString(36)}-${random}`;

    const newPoll: StoragePoll = {
      id: `availability-${Date.now()}-${random}`,
      creator_id: getCurrentUserId(user?.id),
      title: title.trim(),
      description: description.trim() || undefined,
      slug,
      created_at: now,
      updated_at: now,
      status: asDraft ? "draft" : "active",
      type: "availability",
      dates: [],
      settings: {
        ...advancedSettings,
        // Ajouter des dates par défaut pour les availability polls
        selectedDates: generateDefaultDates(),
        // Règles intelligentes d'optimisation
        minLatencyMinutes: schedulingRules.minLatencyMinutes,
        maxLatencyMinutes: schedulingRules.maxLatencyMinutes,
        preferNearTerm: schedulingRules.preferNearTerm,
        preferHalfDays: schedulingRules.preferHalfDays,
        slotDurationMinutes: schedulingRules.slotDurationMinutes,
      },
      // Champs spécifiques aux sondages disponibilités
      clientAvailabilities: undefined,
      parsedAvailabilities: undefined,
      proposedSlots: undefined,
      // Règles intelligentes d'optimisation (legacy, gardé pour compatibilité)
      schedulingRules:
        Object.keys(schedulingRules).length > 0 ? schedulingRules : undefined,
    };

    all.push(newPoll);
    savePolls(all);

    if (onCreate) {
      onCreate(newPoll);
    }

    if (asDraft) {
      toast({
        title: "Brouillon enregistré !",
        description:
          "Votre sondage disponibilités a été sauvegardé comme brouillon.",
      });
      // Ne pas afficher l'écran de succès pour un brouillon
      if (onBack) {
        onBack(newPoll);
      }
      return;
    }

    setPublishedPoll(newPoll);
    setPublished(true);

    toast({
      title: "Sondage créé !",
      description: "Votre sondage disponibilités est maintenant actif.",
    });

    // Si onBack est fourni, on l'appelle avec le poll créé (pour l'intégration workspace)
    if (onBack) {
      onBack(newPoll);
    }
  };

  // Écran de succès après publication
  if (published && publishedPoll) {
    const pollUrl = `${window.location.origin}/poll/${publishedPoll.slug || publishedPoll.id}`;

    return (
      <div className="pb-8">
        <div className="pt-20">
          <div className="max-w-2xl mx-auto p-4 sm:p-6">
            <Card className="bg-gray-800 border-gray-700 shadow-sm">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-green-900/20 rounded-full flex items-center justify-center">
                    <Check className="w-7 h-7 text-green-500" />
                  </div>
                  <div>
                    <CardTitle
                      className="text-2xl text-white"
                      data-testid="success-message"
                    >
                      Sondage Disponibilités créé !
                    </CardTitle>
                    <p className="text-gray-300 mt-1">
                      Partagez le lien avec vos clients pour qu'ils indiquent
                      leurs disponibilités.
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="p-4 bg-gray-700 rounded-lg border border-gray-600">
                  <h3 className="text-white font-semibold mb-2">
                    {publishedPoll.title}
                  </h3>
                  {publishedPoll.description && (
                    <p className="text-gray-300 text-sm">
                      {publishedPoll.description}
                    </p>
                  )}
                </div>

                {!user && <SafeguardSection />}

                <TooltipProvider>
                  <div>
                    <Label
                      htmlFor="share-link"
                      className="text-gray-300 mb-2 block"
                    >
                      Lien de partage :
                    </Label>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <Input
                        id="share-link"
                        value={pollUrl}
                        readOnly
                        className="bg-gray-700 border-gray-600 text-gray-300 font-mono text-sm"
                      />
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            onClick={() => {
                              navigator.clipboard.writeText(pollUrl);
                              toast({
                                title: "Lien copié !",
                                description:
                                  "Le lien a été copié dans le presse-papiers.",
                              });
                            }}
                            size="lg"
                            className="bg-green-600 hover:bg-green-700 text-white"
                            data-testid="availabilitypollcreator-button"
                          >
                            <ExternalLink className="w-4 h-4 mr-2" />
                            Copier
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Copier le lien dans le presse-papiers</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  </div>
                </TooltipProvider>

                <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-600">
                  <Button
                    onClick={() => {
                      console.log("🔗 Lien Debug - Navigation vers dashboard");
                      console.log("🔗 Lien Debug - Route: /dashboard");
                      navigate("/dashboard");
                    }}
                    size="lg"
                    className="bg-green-600 hover:bg-green-700 text-white"
                    data-testid="availabilitypollcreator-navigate"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    Tableau de bord
                  </Button>
                  <Button
                    onClick={() => {
                      console.log("🔗 Lien Debug - Ouverture sondage");
                      console.log("🔗 Lien Debug - URL:", pollUrl);
                      window.open(pollUrl, "_blank");
                    }}
                    size="lg"
                    className="bg-green-600 hover:bg-green-700 text-white"
                    data-testid="availabilitypollcreator-navigate"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Voir le sondage
                  </Button>
                  <Button
                    onClick={() => {
                      console.log("🔗 Lien Debug - Créer un autre sondage");
                      console.log("🔗 Lien Debug - Reset du formulaire");
                      setPublished(false);
                      setPublishedPoll(null);
                      setTitle("");
                      setDescription("");
                      if (onBack) onBack(undefined);
                    }}
                    size="sm"
                    className="bg-green-600 hover:bg-green-700 text-white"
                    data-testid="availabilitypollcreator-crer-un-autre-sondage"
                  >
                    Créer un autre sondage
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-4 sm:p-6 pb-8">
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div>
              <CardTitle className="text-2xl text-white flex items-center gap-2">
                <Calendar className="w-6 h-6 text-green-500" />
                Créer un Sondage Disponibilités
              </CardTitle>
              <p className="text-gray-300 mt-1">
                Vos clients indiquent leurs disponibilités, vous proposez les
                créneaux optimaux.
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <Label htmlFor="title" className="text-gray-300 mb-2 block">
              Titre du sondage *
            </Label>
            <Input
              id="title"
              data-testid="availability-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ex: Planification rendez-vous - Novembre 2025"
              className="bg-gray-700 border-gray-600 text-white"
            />
          </div>

          <div>
            <Label htmlFor="description" className="text-gray-300 mb-2 block">
              Description (optionnel)
            </Label>
            <Textarea
              id="description"
              data-testid="poll-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Ex: Indiquez vos disponibilités pour planifier notre prochain rendez-vous..."
              className="bg-gray-700 border-gray-600 text-white min-h-[100px]"
            />
          </div>

          <SchedulingRulesForm
            rules={schedulingRules}
            onChange={setSchedulingRules}
          />

          <Collapsible open={isAdvancedOpen} onOpenChange={setIsAdvancedOpen}>
            <CollapsibleTrigger asChild>
              <motion.button
                whileTap={{ scale: 0.98 }}
                className="w-full flex items-center justify-between p-4 bg-gray-700/50 rounded-lg border border-gray-600 hover:bg-gray-700 transition-colors focus:ring-2 focus:ring-green-500/50 outline-none"
              >
                <div className="flex items-center gap-2">
                  <SettingsIcon className="w-5 h-5 text-gray-300" />
                  <h3 className="text-lg font-semibold text-white">
                    Paramètres avancés
                  </h3>
                </div>
                {isAdvancedOpen ? (
                  <ChevronDown className="w-5 h-5 text-gray-300" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-gray-300" />
                )}
              </motion.button>
            </CollapsibleTrigger>

            <CollapsibleContent className="mt-2">
              <div className="border border-gray-700 rounded-lg p-4">
                <PollSettingsForm
                  settings={advancedSettings}
                  onSettingsChange={(newSettings) =>
                    setAdvancedSettings(newSettings as AvailabilityPollSettings)
                  }
                  pollType="availability"
                />
              </div>
            </CollapsibleContent>
          </Collapsible>

          <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-600">
            <Button
              onClick={() => handleCreate(false)}
              disabled={!title.trim()}
              size="lg"
              className="bg-green-600 hover:bg-green-700 text-white flex-1"
              data-testid="availabilitypollcreator-button"
            >
              <Check className="w-4 h-4 mr-2" />
              Créer le sondage
            </Button>
            <Button
              onClick={() => handleCreate(true)}
              disabled={!title.trim()}
              size="sm"
              variant="outline"
              className="border-gray-600 text-gray-300 hover:bg-gray-700"
              data-testid="availabilitypollcreator-enregistrer-le-brouillon"
            >
              Enregistrer le brouillon
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
